'use client'

import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import type { CSSProperties, ReactNode } from 'react'

export type AnnouncementVariant = 'default' | 'promo' | 'urgent' | 'info' | 'custom'
export type AnnouncementAnimation = 'none' | 'slide' | 'fade' | 'pulse'

export type AnnouncementItem = {
  id: string
  message: string
  link_url: string | null
  link_label: string | null
  variant: AnnouncementVariant
  custom_bg: string | null
  custom_fg: string | null
  icon: string | null
  animation: AnnouncementAnimation
  dismissible: boolean
}

const VARIANT_CLASSES: Record<Exclude<AnnouncementVariant, 'custom'>, string> = {
  default: 'bg-[var(--color-cta)] text-[var(--color-cta-text)]',
  promo: 'bg-[var(--color-announce-promo,#7c3aed)] text-white',
  urgent: 'bg-[var(--color-announce-urgent,#dc2626)] text-white',
  info: 'bg-[var(--color-announce-info,#1f2937)] text-white',
}

const ANIMATION_CLASSES: Record<Exclude<AnnouncementAnimation, 'none'>, string> = {
  slide: 'motion-safe:animate-[announce-slide_400ms_ease-out]',
  fade: 'motion-safe:animate-[announce-fade_400ms_ease-out]',
  pulse: 'motion-safe:animate-[announce-pulse_2s_ease-in-out_infinite]',
}

export type AnnouncementBarItemProps = {
  item: AnnouncementItem
  showControls?: boolean
  onPrev?: () => void
  onNext?: () => void
  onDismiss?: () => void
}

export function AnnouncementBarItem({
  item,
  showControls = false,
  onPrev,
  onNext,
  onDismiss,
}: AnnouncementBarItemProps) {
  const variantClass = item.variant === 'custom' ? '' : VARIANT_CLASSES[item.variant]
  const customStyle: CSSProperties | undefined =
    item.variant === 'custom'
      ? {
          backgroundColor: item.custom_bg ?? undefined,
          color: item.custom_fg ?? undefined,
        }
      : undefined
  const animationClass = item.animation === 'none' ? '' : ANIMATION_CLASSES[item.animation]

  const messageNode = (
    <>
      {item.icon && <span aria-hidden className="mr-2">{item.icon}</span>}
      <span>{item.message}</span>
    </>
  )

  let content: ReactNode
  if (item.link_url && item.link_label) {
    content = (
      <span className="inline-flex items-center gap-3">
        {messageNode}
        <a
          href={item.link_url}
          className="underline underline-offset-2 font-medium hover:opacity-80 transition-opacity"
        >
          {item.link_label}
        </a>
      </span>
    )
  } else if (item.link_url) {
    content = (
      <a
        href={item.link_url}
        className="underline underline-offset-2 hover:opacity-80 transition-opacity"
      >
        {messageNode}
      </a>
    )
  } else {
    content = <>{messageNode}</>
  }

  return (
    <div
      className={`text-sm py-2 px-4 relative ${variantClass} ${animationClass}`.trim()}
      data-component="AnnouncementBarItem"
      data-announcement-id={item.id}
      style={{ fontFamily: 'var(--font-body)', ...customStyle }}
    >
      <div className="flex items-center justify-center gap-3" aria-live="polite">
        {showControls && (
          <button
            type="button"
            onClick={onPrev}
            className="opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Previous announcement"
          >
            <ChevronLeft size={16} />
          </button>
        )}
        <div className="text-center">{content}</div>
        {showControls && (
          <button
            type="button"
            onClick={onNext}
            className="opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Next announcement"
          >
            <ChevronRight size={16} />
          </button>
        )}
      </div>
      {item.dismissible && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-3 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100 transition-opacity"
          aria-label="Dismiss announcement"
        >
          <X size={14} />
        </button>
      )}
    </div>
  )
}
